import React from 'react'
import type { MonthViewProps, CalendarEvent } from '../types'
import { STATUS_CONFIG } from '../types'
import {
  startOfMonth,
  startOfWeek,
  addDays,
  isToday,
  getEventsForDay,
} from '../utils/time'
import { getDayNameNarrow } from '../utils/locale'

export const MonthView: React.FC<MonthViewProps> = ({
  month,
  events,
  locale,
  onEventClick,
  onDayClick,
}) => {
  // Calculate the calendar grid
  const monthStart = startOfMonth(month)
  const calendarStart = startOfWeek(monthStart)

  // Generate all days to show (6 weeks max)
  const days: Date[] = []
  let currentDay = calendarStart
  while (days.length < 42) {
    days.push(new Date(currentDay))
    currentDay = addDays(currentDay, 1)
  }

  // Split into weeks
  const weeks: Date[][] = []
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7))
  }

  // Only show weeks that have at least one day from this month
  const relevantWeeks = weeks.filter((week) =>
    week.some((day) => day.getMonth() === month.getMonth())
  )

  const handleDayClick = (day: Date) => {
    onDayClick?.(day)
  }

  const handleEventClick = (event: CalendarEvent, e: React.MouseEvent) => {
    e.stopPropagation()
    onEventClick?.(event)
  }

  return (
    <div className="calendar-month-view">
      {/* Day Names Header */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
        }}
      >
        {[1, 2, 3, 4, 5, 6, 0].map((dayIndex) => (
          <div
            key={dayIndex}
            style={{
              padding: '12px 8px',
              textAlign: 'center',
              fontSize: '12px',
              fontWeight: 500,
              color: '#6b7280',
              textTransform: 'uppercase',
            }}
          >
            {getDayNameNarrow(dayIndex, locale)}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateRows: `repeat(${relevantWeeks.length}, 1fr)`,
        }}
      >
        {relevantWeeks.map((week, weekIndex) => (
          <div
            key={weekIndex}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              minHeight: '100px',
            }}
          >
            {week.map((day) => {
              const isCurrentMonth = day.getMonth() === month.getMonth()
              const dayIsToday = isToday(day)
              const dayEvents = getEventsForDay(events, day)
              const maxEventsToShow = 3
              const hasMore = dayEvents.length > maxEventsToShow

              return (
                <div
                  key={day.toISOString()}
                  style={{
                    padding: '4px',
                    borderRight: '1px solid #e5e7eb',
                    borderBottom: '1px solid #e5e7eb',
                    backgroundColor: !isCurrentMonth
                      ? '#f9fafb'
                      : dayIsToday
                      ? '#fef3c7'
                      : undefined,
                    cursor: 'pointer',
                    minHeight: '80px',
                  }}
                  onClick={() => handleDayClick(day)}
                  className="calendar-month-day"
                >
                  {/* Day Number */}
                  <div
                    style={{
                      fontSize: '14px',
                      fontWeight: dayIsToday ? 700 : 400,
                      color: !isCurrentMonth
                        ? '#9ca3af'
                        : dayIsToday
                        ? '#1e3a5f'
                        : '#111827',
                      marginBottom: '4px',
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <span
                      style={{
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        backgroundColor: dayIsToday ? '#1e3a5f' : undefined,
                        color: dayIsToday ? 'white' : undefined,
                      }}
                    >
                      {day.getDate()}
                    </span>
                  </div>

                  {/* Events */}
                  <div style={{ overflow: 'hidden' }}>
                    {dayEvents.slice(0, maxEventsToShow).map((event) => {
                      const statusConfig = STATUS_CONFIG[event.status]

                      return (
                        <div
                          key={event.id}
                          style={{
                            fontSize: '11px',
                            padding: '2px 4px',
                            marginBottom: '2px',
                            backgroundColor: statusConfig.bgColor,
                            borderLeft: `3px solid ${statusConfig.borderColor}`,
                            borderRadius: '2px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            color: statusConfig.textColor,
                            cursor: 'pointer',
                          }}
                          onClick={(e) => handleEventClick(event, e)}
                          title={`${event.patient.name} - ${event.doctor.name}`}
                        >
                          {event.patient.name}
                        </div>
                      )
                    })}

                    {hasMore && (
                      <div
                        style={{
                          fontSize: '10px',
                          color: '#6b7280',
                          padding: '2px 4px',
                        }}
                      >
                        +{dayEvents.length - maxEventsToShow}{' '}
                        {locale === 'sr' ? 'još' : 'more'}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
